import { styled } from "@mui/material/styles";
import MuiAppBar from "@mui/material/AppBar";
import { AppBarProps } from "./AppBar";
import React from "react";
import HydroAILogo from "../../../assets/HydroAILogoDarkTeal.svg";
import InnovaseaLogo from "../../../assets/Innovasea_logo_DarkTeal_Powered_By.svg";

/**
 * BrandedLogo
 *
 * Renders the Innovasea logo image. Accepts optional height and style props for customization.
 *
 * @param {object} props
 * @param {number|string} [props.height=40] - The height of the logo image
 * @param {React.CSSProperties} [props.sx] - Additional CSS styles for the image
 */
export const BrandedLogo: React.FC<{
    height?: number | string;
    sx?: React.CSSProperties;
}> = ({ height = 40, sx }) => {
    return (
        <>
            <img
                src={HydroAILogo}
                height={height}
                alt="HydroAI"
                style={{ ...sx }}
            />
            <span style={{ padding: "0 4px" }} />
            <img
                src={InnovaseaLogo}
                height={typeof height === "number" ? height.toString() : height}
                alt="Innovasea"
                style={{
                    ...sx,
                    filter: "brightness(1) invert(0)",
                    marginTop: "10px",
                }}
            />
        </>
    );
};
interface BrandedAppBarProps extends Omit<AppBarProps, "children"> {
    children?: React.ReactNode;
}

const StyledAppBar = styled(MuiAppBar)<{
    position?: "fixed" | "absolute" | "sticky" | "static" | "relative";
    elevation?: number;
}>(({ theme, position = "static" }) => ({
    ...(position === "fixed" && {
        zIndex: theme.zIndex.appBar,
    }),
    boxShadow: "none",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    color: "rgb(216, 220, 227)",
    backgroundColor: "rgb(216, 220, 227)",
}));

/**
 * BrandedAppBar
 *
 * A styled AppBar component for consistent branding. Accepts children for flexible content placement.
 * Inherits all AppBarProps except 'children', which is handled directly.
 *
 * @param {object} props
 * @param {React.ReactNode} [props.children] - Content to render inside the AppBar
 * @param {"fixed"|"absolute"|"sticky"|"static"|"relative"} [props.position="static"] - AppBar position
 * @param {number} [props.elevation] - AppBar elevation
 * @param {string} [props.className] - Additional class names
 */
export const BrandedAppBar: React.FC<BrandedAppBarProps> = ({
    position = "static",
    elevation,
    children,
    className,
}) => {
    return (
        <StyledAppBar
            position={position}
            elevation={elevation}
            className={className}
        >
            {children}
        </StyledAppBar>
    );
};
